<template>
  <div
    v-if="visible"
    class="word-lookup-popover"
    :style="popoverStyle"
    role="dialog"
    :aria-label="displayWord"
    @click.stop
  >
    <div class="lookup-header">
      <div class="lookup-heading">
        <strong class="lookup-word-title">{{ displayWord }}</strong>
        <span v-if="showLemma" class="lookup-lemma">{{ $t('vocabulary.baseForm') }} {{ result.lemma }}</span>
      </div>
      <button
        type="button"
        class="lookup-icon-button"
        :aria-label="$t('common.close')"
        @click="$emit('close')"
      >
        <span class="lookup-icon icon-close"></span>
      </button>
    </div>

    <div class="lookup-pronunciation">
      <button
        type="button"
        class="lookup-audio"
        :class="{ playing: isPlayingAudio }"
        :title="$t('vocabulary.listen')"
        :aria-label="$t('vocabulary.listen')"
        @click="playAudio"
      >
        <span class="lookup-icon icon-volume"></span>
      </button>
      <span v-if="result?.phonetic" class="lookup-phonetic">{{ result.phonetic }}</span>
      <span v-else-if="loading" class="lookup-skeleton skeleton-phonetic"></span>
    </div>

    <div v-if="loading" class="lookup-loading" :aria-label="$t('vocabulary.loading')" aria-busy="true">
      <span class="lookup-skeleton skeleton-gloss"></span>
      <span class="lookup-skeleton skeleton-line"></span>
      <span class="lookup-skeleton skeleton-line short"></span>
    </div>

    <div v-else-if="error" class="lookup-error-block">
      <p class="lookup-error">{{ error }}</p>
      <button type="button" class="lookup-secondary" @click="$emit('retry')">
        {{ $t('vocabulary.retry') }}
      </button>
    </div>

    <div v-else-if="result" class="lookup-body">
      <div class="lookup-gloss-row">
        <span v-if="result.partOfSpeech" class="lookup-pos">{{ result.partOfSpeech }}</span>
        <span class="lookup-gloss">{{ result.translation || $t('vocabulary.noTranslation') }}</span>
      </div>

      <div v-if="result.contextMeaning" class="lookup-section">
        <span class="lookup-section-label">{{ $t('vocabulary.inContext') }}</span>
        <p class="lookup-context">{{ result.contextMeaning }}</p>
      </div>

      <div v-if="senses.length" class="lookup-section lookup-senses">
        <span class="lookup-section-label">{{ $t('vocabulary.otherSenses') }}</span>
        <ul>
          <li v-for="(sense, index) in senses" :key="index">
            <span v-if="sense.partOfSpeech" class="lookup-sense-pos">{{ sense.partOfSpeech }}</span>
            <span>{{ sense.definition }}</span>
          </li>
        </ul>
      </div>

      <div class="lookup-footer">
        <p v-if="saveError" class="lookup-error">{{ saveError }}</p>
        <button
          type="button"
          class="lookup-save"
          :class="{ saved: isSaved }"
          :disabled="isSaved || saving"
          @click="$emit('save')"
        >
          <span v-if="isSaved" class="lookup-icon icon-check"></span>
          {{ saveLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { playVocabularyAudio } from '@/utils/vocabularyAudio'

const POPOVER_WIDTH = 320
const VIEWPORT_GAP = 12
const MIN_SPACE_BELOW = 320

export default {
  name: 'WordLookupPopover',
  emits: ['close', 'save', 'retry'],
  props: {
    visible: { type: Boolean, default: false },
    word: { type: String, default: '' },
    result: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    saveError: { type: String, default: '' },
    position: { type: Object, default: null },
  },
  data() {
    return {
      isPlayingAudio: false,
    }
  },
  computed: {
    displayWord() {
      return this.result?.displayWord || this.word
    },
    showLemma() {
      const lemma = String(this.result?.lemma || '').trim().toLowerCase()
      return Boolean(lemma) && lemma !== String(this.displayWord).trim().toLowerCase()
    },
    senses() {
      return (this.result?.definitions || []).slice(0, 3)
    },
    isSaved() {
      return Boolean(this.result?.saved)
    },
    saveLabel() {
      if (this.saving) return this.$t('vocabulary.saving')
      if (this.isSaved) return this.$t('vocabulary.saved')
      return this.$t('vocabulary.addToVocabulary')
    },
    popoverStyle() {
      if (typeof window === 'undefined') return {}
      const position = this.position || {}
      const anchorLeft = Number(position.left || 0)
      const anchorWidth = Number(position.width || 0)
      const anchorTop = Number(position.top || 0)
      const anchorBottom = Number(position.bottom || 0)
      const left = Math.min(
        Math.max(anchorLeft + anchorWidth / 2 - POPOVER_WIDTH / 2, VIEWPORT_GAP),
        Math.max(window.innerWidth - POPOVER_WIDTH - VIEWPORT_GAP, VIEWPORT_GAP),
      )
      const spaceBelow = window.innerHeight - anchorBottom
      if (spaceBelow < MIN_SPACE_BELOW && anchorTop > spaceBelow) {
        return {
          left: `${left}px`,
          bottom: `${Math.max(window.innerHeight - anchorTop + 8, VIEWPORT_GAP)}px`,
          maxHeight: `${Math.max(anchorTop - 8 - VIEWPORT_GAP, 160)}px`,
        }
      }
      const top = Math.max(anchorBottom + 8, VIEWPORT_GAP)
      return {
        left: `${left}px`,
        top: `${top}px`,
        maxHeight: `${Math.max(window.innerHeight - top - VIEWPORT_GAP, 160)}px`,
      }
    },
  },
  watch: {
    visible(visible) {
      if (visible) document.addEventListener('keydown', this.handleKeydown)
      else document.removeEventListener('keydown', this.handleKeydown)
    },
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    handleKeydown(event) {
      if (event.key === 'Escape') this.$emit('close')
    },
    async playAudio() {
      this.isPlayingAudio = true
      try {
        await playVocabularyAudio(this.result?.audioUrl || '', this.displayWord)
      } catch (error) {
        console.warn('Pronunciation playback failed:', error)
      } finally {
        this.isPlayingAudio = false
      }
    },
  },
}
</script>

<style scoped>
.word-lookup-popover {
  position: fixed;
  z-index: 50;
  width: 320px;
  max-width: calc(100vw - 24px);
  overflow-y: auto;
  padding: var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  color: var(--ink-primary);
  box-shadow: var(--shadow-float);
  text-align: left;
  font-family: var(--font-family-base);
}

.lookup-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.lookup-heading {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
}

.lookup-word-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
  overflow-wrap: anywhere;
}

.lookup-lemma {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.lookup-icon-button {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  margin: -6px -6px 0 0;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-tertiary);
  cursor: pointer;
}

.lookup-icon-button:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.lookup-pronunciation {
  min-height: 32px;
  margin-top: var(--space-2);
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.lookup-audio {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-secondary);
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease, border-color 160ms ease;
}

.lookup-audio:hover,
.lookup-audio.playing {
  border-color: var(--border-active);
  background: var(--surface-active);
  color: var(--signal-active-strong);
}

.lookup-phonetic {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.4;
}

.lookup-icon {
  display: block;
  width: 14px;
  height: 14px;
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.icon-close { mask-image: url('~@/assets/ui-icons/close.svg'); -webkit-mask-image: url('~@/assets/ui-icons/close.svg'); }
.icon-volume { mask-image: url('~@/assets/ui-icons/volume.svg'); -webkit-mask-image: url('~@/assets/ui-icons/volume.svg'); }
.icon-check { mask-image: url('~@/assets/ui-icons/check.svg'); -webkit-mask-image: url('~@/assets/ui-icons/check.svg'); }

.lookup-body,
.lookup-loading,
.lookup-error-block {
  margin-top: var(--space-3);
}

.lookup-gloss-row {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}

.lookup-pos,
.lookup-sense-pos {
  flex: 0 0 auto;
  padding: 1px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--surface-subtle);
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  font-weight: 500;
  line-height: 1.5;
}

.lookup-gloss {
  font-size: var(--font-size-lg);
  font-weight: 600;
  line-height: 1.4;
}

.lookup-section {
  margin-top: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-subtle);
}

.lookup-section-label {
  display: block;
  margin-bottom: var(--space-1);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 500;
}

.lookup-context {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.lookup-senses ul {
  margin: 0;
  padding: 0;
  display: grid;
  gap: var(--space-2);
  list-style: none;
}

.lookup-senses li {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.lookup-footer {
  margin-top: var(--space-4);
  display: grid;
  gap: var(--space-2);
}

.lookup-save,
.lookup-secondary {
  min-height: 36px;
  padding: 0 var(--space-4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.lookup-save {
  width: 100%;
  border: 1px solid var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.lookup-save:hover:not(:disabled) {
  opacity: 0.88;
}

.lookup-save:disabled {
  cursor: default;
}

.lookup-save.saved {
  border-color: var(--border-default);
  background: var(--surface-subtle);
  color: var(--color-success-text);
}

.lookup-secondary {
  border: 1px solid var(--border-default);
  background: var(--surface-panel);
  color: var(--ink-primary);
}

.lookup-secondary:hover {
  background: var(--surface-subtle);
}

.lookup-save:focus-visible,
.lookup-secondary:focus-visible,
.lookup-audio:focus-visible,
.lookup-icon-button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.lookup-error-block {
  display: grid;
  justify-items: start;
  gap: var(--space-2);
}

.lookup-error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.lookup-loading {
  display: grid;
  gap: var(--space-2);
}

.lookup-skeleton {
  display: block;
  height: 12px;
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, var(--surface-subtle) 0%, var(--color-border) 50%, var(--surface-subtle) 100%);
  background-size: 200% 100%;
  animation: lookup-shimmer 1.2s ease-in-out infinite;
}

.skeleton-phonetic { width: 88px; }
.skeleton-gloss { width: 55%; height: 20px; }
.skeleton-line { width: 100%; }
.skeleton-line.short { width: 70%; }

@keyframes lookup-shimmer {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .lookup-skeleton {
    animation: none;
  }
}

@media (max-width: 640px) {
  .word-lookup-popover {
    left: 12px !important;
    right: 12px;
    top: auto !important;
    bottom: 76px !important;
    width: auto;
    max-height: 60vh !important;
  }
}
</style>
